<template>
  <div class="content__body" ref="scrollContent">
    <content-box class="content__my-info" :width="'800px'">
      <div class="title--wrap">
        <i class="icon userinfo--person"></i>
        <p class="title" v-html="$t('userInfo.myInformation.default.title.changeInfo')"></p>
      </div>
      <div class="form--wrap">
        <form>
          <fieldset>
            <div class="input__id">
              <label for="userId">{{ $t('common.id') }}</label>
              <input id="userId" type="text" name="user-id"
                     readonly
                     :value="userId">
            </div>
            <div class="input__user-name">
              <label for="userName">{{ $t('common.company') }}</label>
              <input id="userName" type="text" name="user-name"
                     autocomplete="user-name"
                     v-model.trim="model.userName"
                     :placeholder="$t('common.placeHolder.enterCompany')"
                     :class="{error: modelError.userName}"
                     @focus="modelError.userName = ''">
              <strong v-if="modelError.userName" class="validation-text">
                {{ $t(`common.validationErrorText.${modelError.userName}`) }}</strong>
            </div>
            <div class="input__user-tel">
              <label for="userTel">{{ $t('common.userTel') }}</label>
              <input id="userTel" type="text" name="user-tel"
                     autocomplete="user-tel"
                     v-model.trim="model.userTel"
                     :placeholder="$t('common.placeHolder.enterPhoneNumber')"
                     :class="{error: modelError.userTel}"
                     @focus="modelError.userTel = ''">
              <strong class="validation-text" v-if="modelError.userTel">
                {{ $t(`common.validationErrorText.${modelError.userTel}`) }}</strong>
            </div>
            <div class="input__email-lang">
              <label for="emailLang">
                {{ $t('common.emailLang') }}
              </label>
              <ui-selectbox id="emailLang"
                            width="100%"
                            v-model="model.displayLang"
                            :items="languageList"
                            :hideLabel="true" />
              <p class="description" v-html="$t('account.signUp.try.form.emailLang.desc')"></p>
            </div>
            <div class="input__referral">
              <label for="referral">{{ $t('common.referralCode') }}</label>
              <div class="input-wrap">
                <div class="input-wrap__input">
                  <input id="referral" type="text" name="referral" ref="referralInput"
                     autocomplete="referral"
                     v-model.trim="model.referral"
                     :placeholder="$t('common.placeHolder.enterReferralCode')"
                     :class="[{error: modelError.referral}, {success: isReferralChecked}]"
                     :readonly="isReferralExist"
                     @focus="modelError.referral = ''"
                     @input="changeReferralInput">
                  <i v-if="isReferralChecked" class="icon icon--check-green-size16"></i>
                  <strong v-if="modelError.referral" class="validation-text">{{ modelError.referral }}</strong>
                  <ui-toast v-for="t in toasts" :key="t.id" v-bind="t" @close="removeToast"/>
                </div>
                <button v-if="isReferralExist" type="button" class="copy-btn" @click="copyReferral">
                  <i class="icon icon--copy-dark"></i>
                </button>
                <ui-button v-else :primary3="true" :disabled="!model.referral || isReferralChecked" @click="validateReferral">{{ $t('referral.duplicateCheck') }}</ui-button>
              </div>
            </div>
          </fieldset>
        </form>
      </div>
    </content-box>
    <content-box class="content__password" :width="'800px'">
      <div class="title--wrap">
        <i class="icon userinfo--lock"></i>
        <p class="title" v-html="$t('userInfo.myInformation.default.title.changePassword')"></p>
      </div>
      <div class="form--wrap">
        <form>
          <fieldset>
            <input type="text" hidden disabled autocomplete="off">
            <div class="input__current-password">
              <label for="currentPassword">{{ $t('common.currentPassword') }}</label>
              <input id="currentPassword" type="password" name="current-password"
                     v-model.trim="model.userPw"
                     autocomplete="current-password"
                     :placeholder="$t('common.placeHolder.enterPassword')"
                     :class="{error: modelError.userPw}"
                     @focus="modelError.userPw = ''">
              <i class="icon icon--pw-eye"
                 :class="{on: !toggleInputType}"
                 @click="toggleShowPasswordValue"></i>
              <strong v-if="modelError.userPw" class="validation-text">
                {{ $t(`common.validationErrorText.${modelError.userPw}`) }}</strong>
            </div>
            <div class="input__new-password">
              <label for="newPassword">{{ $t('common.newPassword') }}</label>
              <input id="newPassword" type="password" name="new-password"
                     autocomplete="new-password"
                     v-model.trim="model.userNewPw"
                     :placeholder="$t('common.placeHolder.enter6char')"
                     :class="{error: modelError.userNewPw}"
                     @focus="modelError.userNewPw = ''">
              <i class="icon icon--pw-eye"
                 :class="{on: !toggleInputTypeNewPw}"
                 @click="toggleShowNewPasswordValue"></i>
              <strong v-if="modelError.userNewPw" class="validation-text">
                {{ $t(`common.validationErrorText.${modelError.userNewPw}`) }}</strong>
            </div>
          </fieldset>
        </form>
      </div>
    </content-box>
    <div class="button--wrap">
      <ui-button @click="onMoveDashboard">{{ $t('common.cancel') }}</ui-button>
      <ui-button class="btn__modify" :primary="true" @click="onModify">{{ $t('common.save') }}</ui-button>
    </div>
    <p class="text-withdraw">
      <strong @click="applyWithdraw">{{ $t('userInfo.myInformation.default.withdraw') }}</strong>
    </p>
    <back-to-top :refName="'scrollContent'" />
    <Dimmed v-if="showReferralConfirm" :click-to-close="true" @close="showReferralConfirm = false">
      <referral-confirm :referral-code="model.referral" @confirm="confirmReferral" @close="showReferralConfirm = false" />
    </Dimmed>
  </div>
</template>

<script>
import { EventBus } from '@/libs/event-bus'
import { mapState } from 'vuex'
import { getUserInfo, modify, checkReferral } from '@/api/user'
import { isValidTel } from '@/libs/validation-helper'
import Dimmed from '@/components/ui/Dimmed'
import ContentBox from '@/components/ui/ContentBox'
import UiButton from '@/components/ui/UiButton'
import BackToTop from '@/components/ui/BackToTop'
import UiSelectbox from '@/components/ui/UiSelectbox'
import UiToast from '@/components/ui/UiToast'
import AppDialog from '@/components/ui/AppDialog'
import ReferralConfirm from '@/components/user-info/my-information/ReferralConfirm'
import { create } from 'vue-modal-dialogs'

const openDialog = create(AppDialog)

export default {
  components: {
    ContentBox,
    UiButton,
    BackToTop,
    UiSelectbox,
    ReferralConfirm,
    Dimmed,
    UiToast
  },
  data () {
    return {
      userId: null,
      browserLang: '',
      languageList: [
        {
          value: 'ko',
          text: '한국어'
        },
        {
          value: 'en',
          text: 'English'
        },
        {
          value: 'ja',
          text: '日本語'
        }
      ],
      model: {
        userName: null,
        userTel: null,
        displayLang: 'ko',
        referral: null,
        timezone: null,
        timezoneName: 'Asia/Seoul',
        userPw: '',
        userNewPw: ''
      },
      modelError: {
        userName: null,
        userTel: null,
        referral: null,
        userPw: null,
        userNewPw: null
      },
      toggleInputType: true,
      toggleInputTypeNewPw: true,
      isReferralExist: false,
      isReferralChecked: false,
      showReferralConfirm: false,
      toasts: [],
      toastId: 0
    }
  },
  computed: {
    ...mapState([
      'userData'
    ])
  },
  mounted () {
    if (this.$route.query.fromReferral) {
      this.$nextTick(() => {
        this.$refs.referralInput.focus()
        const { fromReferral, ...restQuery } = this.$route.query
        this.$router.replace({ query: restQuery })
      })
    }
  },
  methods: {
    applyWithdraw () {
      this.$emit('applyWithdraw')
    },
    onMoveDashboard () {
      this.$router.push({ name: 'dashboard' })
    },
    selectedTimezone (value) {
      this.model.timezone = value.offset
      this.model.timezoneName = value.timezone
    },
    toggleShowPasswordValue () {
      const targetElement = document.querySelector('#currentPassword')
      if (targetElement.attributes.type.value === 'password') {
        targetElement.setAttribute('type', 'text')
        this.toggleInputType = false
      } else {
        targetElement.setAttribute('type', 'password')
        this.toggleInputType = true
      }
    },
    toggleShowNewPasswordValue () {
      const targetElement = document.querySelector('#newPassword')
      if (targetElement.attributes.type.value === 'password') {
        targetElement.setAttribute('type', 'text')
        this.toggleInputTypeNewPw = false
      } else {
        targetElement.setAttribute('type', 'password')
        this.toggleInputTypeNewPw = true
      }
    },
    validateReferral () {
      const pattern = /^[A-Za-z0-9_-]+$/
      if (!pattern.test(this.model.referral)) {
        this.modelError.referral = this.$t('referral.inputError.regexError')
      } else if (this.model.referral.length > 12) {
        this.modelError.referral = this.$t('referral.inputError.tooLong')
      } else {
        checkReferral({ referral: this.model.referral }).then(response => {
          if (!response) {
            this.modelError.referral = this.$t('referral.inputError.duplicate')
          } else {
            this.modelError.referral = ''
            this.showReferralConfirm = true
          }
        })
      }
    },
    changeReferralInput () {
      this.model.terms = null
      this.isReferralChecked = false
    },
    confirmReferral (terms) {
      this.isReferralChecked = true
      this.model.terms = terms
      this.showReferralConfirm = false
    },
    copyReferral () {
      navigator.clipboard.writeText(this.model.referral).then(() => {
        this.toasts.push({
          id: this.toastId++,
          message: this.$t('referral.referrals.copied')
        })
      })
    },
    removeToast (id) {
      this.toasts = this.toasts.filter(toast => toast.id !== id)
    },
    onModify () {
      let allPass = true

      if (this.model.referral && (!this.isReferralChecked && !this.isReferralExist)) {
        this.modelError.referral = this.$t('referral.inputError.needConfirm')
        allPass = false
      }

      if (!this.model.userName) {
        this.modelError.userName = 'requiredCompany'
        allPass = false
      }

      if (!this.model.userTel) {
        this.modelError.userTel = 'requiredTel'
        allPass = false
      } else if (!isValidTel(this.model.userTel)) {
        this.modelError.userTel = 'diffFormatTel'
        allPass = false
      }

      if (this.model.userPw || this.model.userNewPw) {
        if (!this.model.userPw) {
          this.modelError.userPw = 'requiredPassword'
          allPass = false
        }
        if (!this.model.userNewPw) {
          this.modelError.userNewPw = 'requiredPassword'
          allPass = false
        } else if (this.model.userNewPw.length < 6) {
          this.modelError.userNewPw = 'passwordInsufficient'
          allPass = false
        }
      }

      if (allPass) {
        let param = {
          ...this.model
        }
        modify(param, this.userId).then(() => {
          openDialog({
            dialogType: 'userInfo.myInformationEditSuccess'
          }).finally(() => {
            this.$emit('reload')
          })
        }).catch(err => {
          if (err.message === 'Wrong password') {
            this.modelError.userPw = 'wrongPassword'
          }
        })
      }
    }
  },
  created () {
    let userLanguage = navigator.language
    userLanguage = userLanguage.substring(0, 2)
    this.browserLang = userLanguage

    getUserInfo().then(response => {
      this.userId = response.userId
      this.model.userName = response.userName
      this.model.referral = response.referral
      this.model.timezone = response.timezone
      this.model.timezoneName = response.timezoneName
      this.model.userTel = response.phoneNumber
      this.model.displayLang = response.displayLang || this.browserLang
      this.isReferralExist = Boolean(response.referral)
      EventBus.$emit('onLoading', false)
    })
  }
}
</script>

<style lang="scss" scoped="scoped">
.content__body {
  height: calc(100% - 79px);
  padding: 30px 30px 0;
  overflow-y: auto;

  & > [class*=content__] {
    position: relative;
    padding: 50px 40px 50px 230px;
    box-sizing: border-box;
    margin: 0 auto;

    .title--wrap {
      position: absolute;
      top: 40px;
      left: 40px;

      .title {
        font-size: 15px;
        font-weight: 500;
        line-height: 1.47;
        color: #2f2f2f;
        margin-top: 14px;
      }
    }

    .form--wrap {
      [class*=input__] {
        position: relative;
        margin-top: 40px;

        &.input__id,
        &.input__current-password {
          margin-top: 0;
        }
        &.input__current-password,
        &.input__new-password {
          input:focus {
            & + .bubble {
              opacity: 1;
            }
          }
          .icon--pw-eye {
            position: absolute;
            top: 19px;
            right: 5px;
            padding: 5px;
          }
          .bubble {
            opacity: 0;
            width: 250px;
            position: absolute;
            top: 0;
            left: 50%;
            transform: translate(-50%, -100%);

            /deep/ span {
              white-space: normal;
              text-align: left;
            }
          }
        }

        &.input__current-password,
        &.input__new-password {
          width: 350px;
        }

        &.input__email-lang {
          label {
            display: flex;
            align-items: center;
            column-gap: 3px;
            margin-bottom: 5px;

            .tooltip {
              height: 17px;

              &.browser-safari {
                transform: translateY(-1px);
              }
            }

            .icon--info-white-size17 {
              vertical-align: middle;
            }
          }

          p {
            padding: 5px 0 0 5px;
          }
        }

        &.input__referral {
          .input-wrap {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-top: 5px;
            .input-wrap__input {
              position: relative;
              width: 250px;
              input[type=text] {
                margin-top: 0;
              }
              .icon {
                position: absolute;
                top: 11px;
                right: 11px;
              }
            }
          }
        }

        &.input__timezone {
          label {
            display: flex;
            align-items: center;
            column-gap: 3px;
            margin-bottom: 5px;

            .tooltip {
              height: 17px;

              &.browser-safari {
                transform: translateY(-1px);
              }
            }

            .icon--info-white-size17 {
              vertical-align: middle;
            }
          }

          p {
            padding: 5px 0 0 5px;
          }
        }

        label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          line-height: 1;
          color: #767676;
          margin-left: 5px;
        }

        input[type=text],
        input[type=password] {
          margin-top: 5px;
          height: 38px;
          border-radius: 6px;
          border: solid 1px #bbbbbb;
          background-color: #ffffff;
          padding-left: 12px;
          padding-right: 40px;
          box-sizing: border-box;
          font-size: 14px;
          color: #2f2f2f;
          width: 100%;

          &:focus {
            border-color: #289eef;
          }

          &.error {
            border-color: $errorColor;
          }

          &.valid {
            border-color: green;
          }

          &.success {
            border-color: #57AB00;
          }
        }

        input[readonly] {
          border: solid 1px #d7d7d7;
          background-color: #e6e6e6;
          cursor: not-allowed;
        }

        .validation-text {
          @include validErrorText;
          position: absolute;
          left: 6px;
          bottom: -17px;
        }

        .copy-btn {
          @include flexXYcenter;
          padding: 11px;
          border-radius: 50px;
          &:hover {
            background-color: #f5f5f5;
          }
        }
      }
    }
  }

  .content__password {
    margin-top: 30px;
  }

  .button--wrap {
    margin-top: 30px;
    text-align: center;

    .ui-btn {
      width: 100px;
    }

    .btn__modify {
      margin-left: 5px;
    }
  }

  .text-withdraw {
    margin-top: 30px;
    font-size: 14px;
    font-weight: 600;
    color: #767676;
    text-align: center;

    strong {
      color: $brandColor;
      cursor: pointer;
      text-decoration: underline;
    }
  }
}
</style>
